import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { useQuery } from "@tanstack/react-query";
import type { SearchResult, SearchResultType } from "@victor/api-client";
import { api } from "../../lib/api";

/**
 * J · Command palette (Cmd+K / Ctrl+K).
 *
 * Recherche globale instantanée cross-modules. Le backend filtre par
 * permissions de l'utilisateur. Navigation clavier ↑/↓ + Enter.
 */
const ROUTE_BY_TYPE: Record<SearchResultType, (id: string) => string> = {
  client: (id) => `/clients?id=${id}`,
  agent: (id) => `/agents/${id}`,
  site: (id) => `/sites?id=${id}`,
  devis: (id) => `/commercial?devis=${id}`,
  facture: (id) => `/facturation?id=${id}`,
  opportunite: (id) => `/opportunites?id=${id}`,
  sous_traitant: (id) => `/sous-traitance?id=${id}`,
  etablissement: () => `/parametres`,
  user: () => `/parametres`,
  document: (id) => `/documents/${id}`,
};

const LABEL_BY_TYPE: Record<SearchResultType, string> = {
  client: "Client",
  agent: "Agent",
  site: "Site",
  devis: "Devis",
  facture: "Facture",
  opportunite: "Opportunité",
  sous_traitant: "Sous-traitant",
  etablissement: "Établissement",
  user: "Utilisateur",
  document: "Document",
};

export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [cursor, setCursor] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((v) => !v);
      }
      if (e.key === "Escape" && open) setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  useEffect(() => {
    if (open) {
      setQ("");
      setCursor(0);
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [open]);

  const debouncedQ = useDebounce(q, 150);
  const res = useQuery({
    queryKey: ["palette", debouncedQ],
    queryFn: () => api.search.global(debouncedQ),
    enabled: open && debouncedQ.trim().length >= 2,
    staleTime: 5000,
  });

  const items: SearchResult[] = res.data?.results ?? [];

  const go = (item: SearchResult) => {
    const path = ROUTE_BY_TYPE[item.type](item.id);
    navigate(path);
    setOpen(false);
  };

  const onInputKey = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setCursor((c) => Math.min(c + 1, items.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setCursor((c) => Math.max(c - 1, 0));
    } else if (e.key === "Enter" && items[cursor]) {
      e.preventDefault();
      go(items[cursor]);
    }
  };

  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-navy-950/40 px-4 pt-[15vh] backdrop-blur-sm"
      onClick={() => setOpen(false)}
    >
      <div
        className="w-full max-w-xl overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-navy-100"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 border-b border-navy-100 px-4 py-3">
          <span className="text-navy-400">⌘K</span>
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setCursor(0);
            }}
            onKeyDown={onInputKey}
            placeholder="Rechercher un client, agent, devis, facture…"
            className="flex-1 bg-transparent text-base text-navy-800 outline-none placeholder:text-navy-400"
          />
        </div>
        <div className="max-h-[50vh] overflow-y-auto">
          {q.trim().length < 2 ? (
            <p className="px-4 py-8 text-center text-sm text-navy-400">
              Tape au moins 2 caractères…
            </p>
          ) : res.isLoading ? (
            <p className="px-4 py-8 text-center text-sm text-navy-400">
              Recherche…
            </p>
          ) : items.length === 0 ? (
            <p className="px-4 py-8 text-center text-sm text-navy-400">
              Aucun résultat.
            </p>
          ) : (
            <ul>
              {items.map((it, i) => (
                <li key={`${it.type}:${it.id}`}>
                  <button
                    type="button"
                    onClick={() => go(it)}
                    onMouseEnter={() => setCursor(i)}
                    className={`flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm ${
                      i === cursor ? "bg-accent-50" : ""
                    }`}
                  >
                    <span className="shrink-0 rounded-full bg-navy-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-navy-600">
                      {LABEL_BY_TYPE[it.type]}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-semibold text-navy-800">
                        {it.label}
                      </span>
                      {it.snippet ? (
                        <span className="block truncate text-xs text-navy-500">
                          {it.snippet}
                        </span>
                      ) : null}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="border-t border-navy-100 bg-navy-50/50 px-4 py-2 text-xs text-navy-500">
          ↑↓ naviguer · Enter ouvrir · Esc fermer
        </div>
      </div>
    </div>
  );
}

function useDebounce<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const id = window.setTimeout(() => setV(value), ms);
    return () => window.clearTimeout(id);
  }, [value, ms]);
  return v;
}
